import React from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Mail, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

export const Contact = () => {
  const { t, lang } = useLanguage();

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#0F172A] py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-slate opacity-30" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400" data-testid="contact-eyebrow">
            {t.contact.eyebrow}
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">{t.contact.subtitle}</p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {/* Phone */}
          <motion.a
            href="tel:01708251779"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0, ease: [0.16, 1, 0.3, 1] }}
            className="group bg-[#0F172A] p-8 lg:p-10 hover:bg-[#111e36] transition-colors"
            data-testid="contact-phone-link"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/10 text-cyan-400 group-hover:bg-cyan-500 group-hover:text-white transition-colors mb-6">
              <Phone size={20} strokeWidth={1.75} />
            </div>
            <div className="text-cyan-400 text-[11px] font-semibold tracking-[0.2em] uppercase">
              {t.contact.info.callTitle}
            </div>
            <div className="mt-3 font-display text-2xl lg:text-3xl font-bold text-white group-hover:text-cyan-300 transition-colors break-all">
              0170 8251779
            </div>
            <div className="mt-2 text-sm text-slate-400">{t.contact.info.callSub}</div>
            <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 group-hover:gap-3 transition-all">
              {t.hero.ctaSecondary}
              <ArrowRight size={14} />
            </div>
          </motion.a>

          {/* Email */}
          <motion.a
            href="mailto:zemka.gebaeudereinigung@gmail.com"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="group bg-[#0F172A] p-8 lg:p-10 hover:bg-[#111e36] transition-colors"
            data-testid="contact-email-link"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/10 text-cyan-400 group-hover:bg-cyan-500 group-hover:text-white transition-colors mb-6">
              <Mail size={20} strokeWidth={1.75} />
            </div>
            <div className="text-cyan-400 text-[11px] font-semibold tracking-[0.2em] uppercase">E-Mail</div>
            <div className="mt-3 font-display text-lg lg:text-xl font-bold text-white group-hover:text-cyan-300 transition-colors break-all">
              zemka.gebaeudereinigung@gmail.com
            </div>
            <div className="mt-2 text-sm text-slate-400">
              {lang === "en" ? "Reply within one business day" : "Antwort innerhalb 1 Werktages"}
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 group-hover:gap-3 transition-all">
              {lang === "en" ? "Send email" : "E-Mail schreiben"}
              <ArrowRight size={14} />
            </div>
          </motion.a>

          {/* Address */}
          <motion.a
            href="https://www.google.com/maps/search/?api=1&query=Sigmundstra%C3%9Fe+82377+Penzberg"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="group bg-[#0F172A] p-8 lg:p-10 hover:bg-[#111e36] transition-colors"
            data-testid="contact-address-link"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/10 text-cyan-400 group-hover:bg-cyan-500 group-hover:text-white transition-colors mb-6">
              <MapPin size={20} strokeWidth={1.75} />
            </div>
            <div className="text-cyan-400 text-[11px] font-semibold tracking-[0.2em] uppercase">
              {t.contact.info.addressTitle}
            </div>
            <div className="mt-3 font-display text-lg lg:text-xl font-bold text-white whitespace-pre-line group-hover:text-cyan-300 transition-colors">
              {t.contact.info.address}
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 group-hover:gap-3 transition-all">
              {lang === "en" ? "Open in Maps" : "In Google Maps öffnen"}
              <ArrowRight size={14} />
            </div>
          </motion.a>
        </div>

        {/* Opening hours bar */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="mt-px bg-white/5 border border-white/10 border-t-0 p-8 lg:p-10"
          data-testid="contact-hours"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-3 text-cyan-400 text-[11px] font-semibold tracking-[0.2em] uppercase">
              <Clock size={14} /> {t.contact.info.hoursTitle}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-10 text-sm text-slate-200">
              <div className="flex items-center justify-between sm:justify-start gap-3">
                <span>{t.contact.info.hoursMonSat}</span>
                <span className="font-semibold text-white">{t.contact.info.hoursMonSatVal}</span>
              </div>
              <div className="flex items-center justify-between sm:justify-start gap-3">
                <span>{t.contact.info.hoursSun}</span>
                <span className="font-semibold text-slate-400">{t.contact.info.hoursSunVal}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
